'use client'
import React, { useEffect, useState } from 'react'
import { CheckIcon } from './Icons'

export const ProgressLoader = ({ onNextStep, data }: { onNextStep: () => void; data: any }) => {
  const items = data.items
  const [active, setActive] = useState<number | null>(null)
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const stepDelay = 2000
  setTimeout(() => {
    onNextStep()
  }, 999999999)
  useEffect(() => {
    setActive(0)
  }, [])
  useEffect(() => {
    if (active === null) return

    if (active < items.length) {
      const timer = setTimeout(() => setActive((prev) => (prev ?? 0) + 1), stepDelay)
      return () => clearTimeout(timer)
    } else {
      setActive(items.length - 1)
      setTimeout(() => {
        onNextStep()
      }, 100)
    }
  }, [active, items.length, onNextStep])

  useEffect(() => {
    if (!items?.length) return

    const totalDuration = stepDelay * items.length
    let start: number | null = null
    let frameId: number

    const animate = (timestamp: number) => {
      if (!start) start = timestamp
      const elapsed = timestamp - start
      const progress = Math.min((elapsed / totalDuration) * 100, 100)
      setAnimatedProgress(progress)

      if (progress < 100) {
        frameId = requestAnimationFrame(animate)
      }
    }

    setAnimatedProgress(0)
    frameId = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(frameId)
  }, [items.length])

  return (
    <div className="mt-14 progress-loader">
      <div className="relative rotate-[72deg]">
        <svg
          className="mx-auto mb-11"
          width="256"
          height="256"
          viewBox="0 0 256 256"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.1"
            d="M130.009 5.13639C130.055 2.30907 132.386 0.0438783 135.209 0.203109C159.733 1.58628 183.382 10.0012 203.303 24.4941C223.224 38.9871 238.509 58.8978 247.374 81.8055C248.394 84.4426 246.956 87.3574 244.281 88.2717C241.605 89.1859 238.705 87.7527 237.675 85.1191C229.498 64.2047 215.495 46.0275 197.279 32.7746C179.062 19.5217 157.458 11.7932 135.042 10.4506C132.219 10.2816 129.963 7.96371 130.009 5.13639Z"
            fill="#349760"
          />
          <path
            opacity="0.1"
            d="M245.471 91.9438C248.174 91.114 251.049 92.6307 251.77 95.3649C258.033 119.116 257.338 144.208 249.71 167.632C242.082 191.057 227.869 211.747 208.822 227.256C206.629 229.042 203.413 228.575 201.716 226.313C200.02 224.05 200.487 220.85 202.674 219.057C220.038 204.817 232.998 185.882 239.973 164.462C246.948 143.042 247.622 120.106 241.972 98.3722C241.261 95.6354 242.768 92.7735 245.471 91.9438Z"
            fill="#349760"
          />
          <path
            opacity="0.1"
            d="M198.592 228.58C200.216 230.894 199.662 234.097 197.285 235.627C176.631 248.923 152.553 256.016 127.918 256C103.283 255.984 79.2132 248.861 58.5769 235.538C56.2012 234.004 55.6512 230.801 57.2786 228.489C58.9061 226.176 62.0944 225.631 64.4754 227.157C83.3839 239.271 105.397 245.745 127.924 245.76C150.451 245.774 172.473 239.328 191.397 227.238C193.78 225.716 196.967 226.265 198.592 228.58Z"
            fill="#349760"
          />
          <path
            opacity="0.1"
            d="M54.157 226.218C52.4577 228.478 49.2407 228.941 47.0502 227.152C28.0229 211.618 13.8367 190.91 6.23914 167.476C-1.35845 144.042 -2.0214 118.949 4.272 95.2059C4.99651 92.4726 7.87301 90.9596 10.5752 91.7928C13.2773 92.626 14.7809 95.4899 14.0659 98.2257C8.38789 119.952 9.03246 142.889 15.98 164.318C22.9275 185.747 35.8635 204.699 53.2092 218.96C55.3934 220.756 55.8562 223.958 54.157 226.218Z"
            fill="#349760"
          />
          <path
            opacity="0.1"
            d="M11.7704 88.1223C9.09574 87.2046 7.66165 84.288 8.68551 81.6522C17.5793 58.7558 32.8903 38.8648 52.8298 24.3975C72.7693 9.93015 96.429 1.54559 120.955 0.193948C123.778 0.0383462 126.106 2.30653 126.149 5.13391C126.191 7.96128 123.932 10.2763 121.109 10.4417C98.6916 11.7554 77.0768 19.4562 58.8434 32.6857C40.61 45.9151 26.5834 64.0743 18.3797 84.9783C17.3467 87.6105 14.4451 89.0399 11.7704 88.1223Z"
            fill="#349760"
          />
          <defs>
            <linearGradient
              id="paint0_linear_2425_2995"
              x1="128"
              y1="92"
              x2="128"
              y2="104"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_2425_2995"
              x1="100"
              y1="0"
              x2="100"
              y2="12"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <clipPath id="clip0_2425_2995">
              <rect width="200" height="72" fill="white" transform="translate(28 92)" />
            </clipPath>
          </defs>
        </svg>
        {items.map((item: any, i: number) => (
          <svg
            key={i}
            className={`absolute top-0 left-1/2 -translate-x-1/2 ${i === active ? 'active-loader' : ''}`}
            width="256"
            height="256"
            viewBox="0 0 256 256"
            style={{ transform: `translateX(-50%) rotate(${21 + 72 * i}deg) scaleX(-1)` }}
          >
            <g transform="rotate(-90 128 128})">
              <circle
                cx="128"
                cy="128"
                r="122.5"
                fill="transparent"
                stroke="#349760"
                strokeWidth="10"
                strokeDasharray="763"
                strokeDashoffset="763"
                strokeLinecap="round"
                className="opacity-0"
              ></circle>
            </g>
          </svg>
        ))}
        <div className="absolute rotate-[-72deg] inline-flex items-center top-1/2 left-1/2 text-center -translate-y-1/2 -translate-x-1/2 justify-center pointer-events-none">
          <span className="text-[62px] font-percent leading-[98px]  font-medium text-buttonGradient">
            {Math.round(animatedProgress) !== 100 ? (
              <>{Math.round(animatedProgress)}%</>
            ) : (
              <CheckIcon />
            )}
          </span>
        </div>
      </div>

      {items.map((item: any, i: number) => (
        <>
          <div key={i + item.title} className={i === active ? 'block' : 'hidden'}>
            <h4
              className="text-center font-bold text-white text-[30px] tracking-[-1px] leading-[1.27]"
              dangerouslySetInnerHTML={{ __html: item.title }}
            ></h4>
            <p
              className="text-[#101A3699] font-medium text-center mt-2"
              dangerouslySetInnerHTML={{ __html: item.description }}
            ></p>
          </div>
        </>
      ))}
    </div>
  )
}
